import React from 'react';
import { TabbedSection, SectionTab, UiAction, EventName } from '../types';
import { useUiAction } from '../ui.actions';
import { SectionProps } from './types';
import { useAnalyticsContext } from '../analytics/analytics.provider';
import { useCorePageState } from '../page/provider';
import classNames from 'classnames';
import { useMobile } from '../context/mobile/mobile.provider';
import { navigation } from '@ecosystem/newron-design-system';

function TabbedSectionElement({ section }: SectionProps) {
  const { tabs, activeTab } = section as TabbedSection;
  const { dsl, dataLoading } = useCorePageState();

  const { perform } = useUiAction();
  const { Tabs } = navigation;

  const { trackPageEvent } = useAnalyticsContext();
  const { isMobile } = useMobile();

  const baseClassNames = 'text-left p-4 border-b';
  const activeClassNames = 'text-brand border-brand border-b-2 ';
  const inactiveClassNames = 'border-light cursor transition-colors';
  const disabledClassNames = 'text-inactive cursor-not-allowed';
  const enabledClassNames = 'hover:bg-medium';

  const getClassName = (tab: SectionTab) => {
    return `${baseClassNames} ${tab.name === activeTab ? activeClassNames : inactiveClassNames} 
    ${dataLoading ? disabledClassNames : enabledClassNames}`;
  };

  const handleTabClick = (selectedTab: string | SectionTab) => {
    const updatedTabs = typeof selectedTab === 'string' ? tabs.find(tab => tab.name === selectedTab) : selectedTab;

    const title = typeof dsl?.heading.title === 'string' ? dsl?.heading.title : '';

    trackPageEvent(EventName.NavigationClicked, {
      linkName: updatedTabs.label,
      navigationSource: `${title} ${getTabLabelFromTabs(tabs, activeTab)}`,
    });

    const uiAction: UiAction = {
      type: 'NAVIGATE_TO_PATH',
      payload: {
        path: updatedTabs.path,
      },
    };
    perform(uiAction);
  };

  const mobileView = () => {
    return (
      <div className={classNames('relative pb-1', { flex: !isMobile, 'items-stretch': !isMobile })}>
        <Tabs currentTab={activeTab} onTabChange={handleTabClick}>
          {tabs.map((tab, idx) => (
            <Tabs.Tab index={idx} id={tab.name} key={tab.name} disabled={dataLoading}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs>
        <span className="flex-grow block border-b border-light"> </span>
      </div>
    );
  };

  return isMobile ? (
    mobileView()
  ) : (
    <div className={classNames('relative flex items-stretch pb-1', { 'flex-wrap': isMobile })}>
      {tabs.map(tab => (
        <button disabled={dataLoading} className={getClassName(tab)} key={tab.name} onClick={() => handleTabClick(tab)}>
          {tab.label}
        </button>
      ))}
      <span className="flex-grow inline-block border-b border-light"> </span>
    </div>
  );
}

export { TabbedSectionElement };

const getTabLabelFromTabs = (tabs: SectionTab[], activeTab: string) => {
  return tabs.find(tab => tab.name === activeTab).label;
};
